<script>
	module.exports = {
		props: {
			page: {
				type: Number,
				default: 0
			},
			pageSize: {
				type: Number,
				default: 25
			},
			total: {
				type: Number,
				required: true
			},
			onPageChange: {
				type: Function,
				default(page) {}
			},
			// Needs to be an odd number
			pagesToShow: {
				type: Number,
				default: 5
			},
			showArrowText: {
				type: Boolean,
				default: true
			},
			label: {
				type: String,
				default: 'Pagination'
			},
			variant: {
				type: String
			},
			allowWrap: {
				type: Boolean,
				default: false
			},
			onNextPage: {
				type: Function,
				default(page) {}
			},
			onPreviousPage: {
				type: Function,
				default(page) {}
			},
			onGoToPage: {
				type: Function,
				default(page) {}
			}
		},
		watch: {
			page(p) {
				this.onPageChange(p);
			}
		},
		computed: {
			pageNumber() {
				const val = this.page;

				if (typeof val !== 'number') {
					return 0;
				}
				return val;
			},
			totalPages() {
				if (!this.total || !this.pageSize) {
					return false;
				}
				return Math.ceil(this.total / this.pageSize);
			},
			onFirstPage() {
				return this.pageNumber === 0;
			},
			onLastPage() {
				return this.pageNumber + 1 === this.totalPages;
			},
			pages() {
				if (this.isMinimal) {
					return [];
				}

				let pages = [];
				let pagesOnEitherSide = (this.pagesToShow - 1) / 2;
				let firstPage = Math.max(this.pageNumber - pagesOnEitherSide, 1);
				let lastPage = Math.min(firstPage + this.pagesToShow - 1, this.totalPages);

				if (this.pagesToShow >= this.totalPages) {
					firstPage = 1;
					lastPage = this.totalPages;
				} else if (lastPage - firstPage < this.pagesToShow && lastPage - this.pagesToShow > 0) {
					firstPage = lastPage - this.pagesToShow + 1;
				}

				for (let i = firstPage; i <= lastPage; i++) {
					pages.push(i);
				}

				return pages;
			},
			summaryText() {
				return `${this.pageNumber + 1} of ${this.totalPages}`;
			},
			isMinimal() {
				return this.variant === 'minimal';
			}
		},
		methods: {
			onNextPageClick(page) {
				let target = page;

				if (target >= this.totalPages) {
					target = 0;
				}

				this.onPageChange(target);
				this.onNextPage(target);
			},
			onPreviousPageClick(page) {
				let target = page;

				if (target < 0) {
					target = this.totalPages - 1;
				}

				this.onPageChange(target);
				this.onPreviousPage(target);
			},
			onGoToPageClick(page) {
				this.onPageChange(page);
				this.onGoToPage(page);
			}
		}
	};
</script>
<style lang="scss">
	@import 'config/colors';
	@import 'config/variables';
	@import 'mixins/media';

	.pagination {
		/* Chrome (until v54)/Safari (necessary as of v17) */
		-webkit-user-select: none;
		/* Firefox (until v69)*/
		-moz-user-select: none;
		user-select: none;
		margin: 0.5em 0 1.5rem;
		font-size: 0.875rem;

		ul {
			display: flex;
			justify-content: center;
			align-items: center;
		}

		li {
			padding: 0.25rem;
			margin: 0 0.25rem;

			.button {
				margin-right: 0;
			}
		}

		.navigation-arrow {
			i {
				font-size: 1rem;
			}

			&.previous {
				i {
					margin-right: 0.25rem;
				}
			}

			&.next {
				i {
					margin-left: 0.25rem;
				}
			}

			.button {
				display: flex;
				align-items: center;

				&:disabled {
					color: $c-slate-dark;
				}
			}
		}

		.arrow-text,
		.ellipsis,
		.pagination-item {
			display: none;

			@include media(med) {
				display: inline-block;
			}
		}

		.pagination-item {
			min-width: 1.5rem;
			text-align: center;
		}

		.pagination-mobile {
			display: inline-block;

			@include media(med) {
				display: none;
			}
		}

		&.minimal {
			display: flex;
			align-items: center;
			font-size: 1rem;
			margin: 0;
			padding: 0;

			.navigation-arrow {
				margin: 0;

				button i {
					color: $c-slate-dark;
					font-size: 1.4rem;
					font-weight: 900;

					&:hover {
						color: $c-blue-dark;
					}
				}
			}
		}
	}
</style>
<template>
	<nav
		class="pagination"
		:class="variant"
		:aria-label="isMinimal ? null : label">
		<span
			v-if="isMinimal && total"
			class="minimal-label margin-right-small"
			aria-live="polite"
			v-text="`${label} ${summaryText}`">
		</span>
		<ul class="list-unstyled">
			<li class="navigation-arrow previous">
				<button
					class="button -link -link-standard"
					:disabled="onFirstPage && !allowWrap"
					@click="onPreviousPageClick(pageNumber - 1)">
					<i
						class="fa-solid fa-chevron-left"
						aria-hidden="true"></i>
					<span
						class="arrow-text"
						:class="{
							'hidden-visually': !showArrowText
						}">
						Previous
					</span>
				</button>
			</li>
			<li
				v-if="pages.length && pages[0] !== 1"
				class="pagination-item">
				<button
					class="button -link -link-standard"
					@click="onGoToPageClick(0)">
					<span class="hidden-visually">Page</span>
					1
				</button>
			</li>
			<li
				v-if="pages.length && pages[0] > 2"
				class="ellipsis">
				...
			</li>
			<li
				v-for="p in pages"
				:key="p"
				class="pagination-item">
				<button
					class="button -link -link-standard"
					:class="{
						active: p === pageNumber + 1
					}"
					:aria-current="p === pageNumber + 1 ? 'page' : false"
					@click="onGoToPageClick(p - 1)">
					<span class="hidden-visually">Page</span>
					{{ p }}
				</button>
			</li>
			<li
				v-if="pages.length && pages[pages.length - 1] < totalPages - 1"
				class="ellipsis">
				...
			</li>
			<li
				v-if="pages.length && pages[pages.length - 1] !== totalPages"
				class="pagination-item">
				<button
					class="button -link -link-standard"
					@click="onGoToPageClick(totalPages - 1)">
					<span class="hidden-visually">Page</span>
					{{ totalPages }}
				</button>
			</li>
			<li
				v-if="!isMinimal"
				class="ellipsis pagination-mobile">
				<span class="hidden-visually"> Page </span>
				<span v-text="summaryText"></span>
			</li>
			<li class="navigation-arrow next">
				<button
					class="button -link -link-standard"
					:disabled="onLastPage && !allowWrap"
					@click="onNextPageClick(pageNumber + 1)">
					<span
						class="arrow-text"
						:class="{
							'hidden-visually': !showArrowText
						}">
						Next
					</span>
					<i
						class="fa-solid fa-chevron-right"
						aria-hidden="true"></i>
				</button>
			</li>
		</ul>
	</nav>
</template>
